import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, ModalFooter } from './Modal';
import { Button } from '@/components/ui/Button';
import { Select, Input, Textarea } from './FormField';
import { formatMonthLabel } from '@/utils/monthLabel';
import { MonthLabel } from '@/components/ui/MonthLabel';
import { FormMultiSelect, type FormMultiSelectOption } from '@/components/ui/FormMultiSelect';
import { DotRating } from '@/components/ui/DotRating';
import { useAppStore } from '@/stores/appStore';
import { useFieldConfig } from '@/hooks/useFieldConfig';
import { batchApi, type BatchStartResult } from '@/services/api';
import { CheckCircleIcon, ExclamationTriangleIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';

interface BatchUpdateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  selectedIds: string[];
  onComplete: () => void;
}

type Step = 'configure' | 'confirm' | 'progress' | 'result';
type TagMode = 'add' | 'replace' | 'remove';

export function BatchUpdateDialog({ isOpen, onClose, selectedIds, onComplete }: BatchUpdateDialogProps) {
  const { products, classifications, statuses, tags, owners, quarters } = useAppStore();
  const fc = useFieldConfig();

  const [step, setStep] = useState<Step>('configure');
  const [field, setField] = useState('');
  const [fieldValue, setFieldValue] = useState(''); // scalar / boolean (as string)
  const [tagMode, setTagMode] = useState<TagMode>('add');
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [configError, setConfigError] = useState<string | null>(null);

  // progress / result state
  const [total, setTotal] = useState(0);
  const [applied, setApplied] = useState(0);
  const [skipped, setSkipped] = useState<BatchStartResult['skipped']>([]);
  const [batchId, setBatchId] = useState<string | null>(null);
  const [rolledBack, setRolledBack] = useState(false);
  const [failed, setFailed] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const stopRef = useRef(false);
  const [stopping, setStopping] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [rollbackFailed, setRollbackFailed] = useState(false);
  // The "Stop & roll back" control is only revealed if a run is slow enough that
  // interrupting it is meaningful. Fast batches finish before this fires, so the
  // danger button never flashes by — the post-run "Undo this batch" covers them.
  const [showStop, setShowStop] = useState(false);

  useEffect(() => {
    if (step !== 'progress') return;
    setShowStop(false);
    const t = setTimeout(() => setShowStop(true), 1200);
    return () => clearTimeout(t);
  }, [step]);

  const MAIN_FIELD_OPTIONS = useMemo(
    () => [
      { value: 'title', label: `${fc.label('initiative')} Title` },
      { value: 'subProductId', label: fc.label('subProduct') },
      { value: 'classificationId', label: fc.label('classification') },
      { value: 'statusId', label: fc.label('status') },
      { value: 'userId', label: fc.label('owner') },
      { value: 'startQuarter', label: `${fc.label('start')} month` },
      { value: 'endQuarter', label: `${fc.label('end')} month` },
      { value: 'tags', label: fc.label('tags') },
      { value: 'isStarred', label: 'Starred' },
      { value: 'description', label: 'Description' },
    ],
    [fc]
  );

  const SCORING_FIELD_OPTIONS = useMemo(
    () => [
      { value: 'impact', label: 'Impact' },
      { value: 'effort', label: 'Effort' },
    ],
    []
  );

  const FIELD_OPTIONS = useMemo(
    () => [...MAIN_FIELD_OPTIONS, ...SCORING_FIELD_OPTIONS],
    [MAIN_FIELD_OPTIONS, SCORING_FIELD_OPTIONS]
  );

  const subProductOptions = useMemo(() => {
    const opts: { value: string; label: string }[] = [];
    products.forEach((p) => {
      (p.subProducts || []).forEach((sp) => {
        opts.push({ value: sp.id, label: `${p.name} > ${sp.name}` });
      });
    });
    return opts;
  }, [products]);

  const ownerOptions = useMemo(
    () => owners.map((u) => ({ value: u.id, label: (u as any).email ? `${u.name} (${(u as any).email})` : u.name })),
    [owners]
  );
  const tagOptions: FormMultiSelectOption[] = useMemo(
    () => tags.map((t) => ({ value: t.id, label: t.name })),
    [tags]
  );
  const classificationOptions = useMemo(
    () => classifications.map((c) => ({ value: c.id, label: c.name })),
    [classifications]
  );
  const statusOptions = useMemo(
    () => statuses.map((s) => ({ value: s.id, label: s.name })),
    [statuses]
  );
  const quarterOptions = useMemo(
    () => quarters.map((q) => ({ value: q.key, label: q.fullLabel })),
    [quarters]
  );

  function resetAll() {
    setStep('configure');
    setField('');
    setFieldValue('');
    setTagMode('add');
    setSelectedTagIds([]);
    setConfigError(null);
    setTotal(0);
    setApplied(0);
    setSkipped([]);
    setBatchId(null);
    setRolledBack(false);
    setFailed(false);
    setRunError(null);
    stopRef.current = false;
    setStopping(false);
    setUndoing(false);
    setRollbackFailed(false);
  }

  function handleClose() {
    resetAll();
    onClose();
  }

  function onFieldChange(f: string) {
    setField(f);
    setFieldValue(f === 'isStarred' ? 'true' : '');
    setSelectedTagIds([]);
    setTagMode('add');
    setConfigError(null);
  }

  // Human-readable preview of the chosen value (for the confirm screen)
  const valueDisplay = useMemo(() => {
    if (field === 'tags') {
      const names = selectedTagIds
        .map((id) => tags.find((t) => t.id === id)?.name)
        .filter(Boolean)
        .join(', ');
      return `${tagMode} → ${names}`;
    }
    if (field === 'isStarred') return fieldValue === 'true' ? 'Starred' : 'Not starred';
    if (field === 'userId') return ownerOptions.find((o) => o.value === fieldValue)?.label ?? '(cleared)';
    if (field === 'subProductId') return subProductOptions.find((o) => o.value === fieldValue)?.label ?? '';
    if (field === 'classificationId') return classificationOptions.find((o) => o.value === fieldValue)?.label ?? '(cleared)';
    if (field === 'statusId') return statusOptions.find((o) => o.value === fieldValue)?.label ?? '';
    if (field === 'startQuarter' || field === 'endQuarter')
      return field === 'endQuarter' && !fieldValue ? '(cleared)' : formatMonthLabel(quarterOptions.find((o) => o.value === fieldValue)?.label ?? '');
    if (field === 'description' && !fieldValue) return '(cleared)';
    if (field === 'impact' || field === 'effort') return fieldValue ? `${fieldValue} / 5` : '(cleared)';
    return fieldValue;
  }, [field, fieldValue, tagMode, selectedTagIds, tags, ownerOptions, subProductOptions, classificationOptions, statusOptions, quarterOptions]);

  const fieldLabel = FIELD_OPTIONS.find((o) => o.value === field)?.label ?? '';

  function validateConfigure(): boolean {
    if (!field) {
      setConfigError('Choose a field to update');
      return false;
    }
    if (field === 'tags') {
      if (selectedTagIds.length === 0) {
        setConfigError('Select at least one tag');
        return false;
      }
      return true;
    }
    if (field === 'title') {
      if (fieldValue.trim().length < 3 || fieldValue.trim().length > 200) {
        setConfigError('Title must be 3–200 characters');
        return false;
      }
      return true;
    }
    // Nullable fields may be empty (clears the value).
    if (
      field === 'endQuarter' ||
      field === 'description' ||
      field === 'classificationId' ||
      field === 'userId' ||
      field === 'impact' ||
      field === 'effort'
    )
      return true;
    if (!fieldValue) {
      setConfigError('Choose a value');
      return false;
    }
    return true;
  }

  function resolvedValue(): unknown {
    if (field === 'tags') return selectedTagIds;
    if (field === 'isStarred') return fieldValue === 'true';
    if (field === 'impact' || field === 'effort') return fieldValue ? parseInt(fieldValue, 10) : null;
    return fieldValue;
  }

  async function runBatch() {
    setStep('progress');
    setRunError(null);
    setFailed(false);
    setRolledBack(false);
    setRollbackFailed(false);
    stopRef.current = false;
    setApplied(0);
    // Tracked outside the try so the catch can roll back any applied chunks.
    let createdBatchId: string | null = null;
    try {
      const start = await batchApi.start({
        field,
        value: resolvedValue(),
        tagMode: field === 'tags' ? tagMode : undefined,
        initiativeIds: selectedIds,
      });
      createdBatchId = start.batchId;
      setBatchId(start.batchId);
      setTotal(start.total);
      setSkipped(start.skipped);

      const skippedSet = new Set(start.skipped.map((s) => s.id));
      const eligibleIds = selectedIds.filter((id) => !skippedSet.has(id));

      for (let i = 0; i < eligibleIds.length; i += start.chunkSize) {
        if (stopRef.current) {
          await batchApi.rollback(start.batchId);
          setRolledBack(true);
          setStep('result');
          return;
        }
        const chunk = eligibleIds.slice(i, i + start.chunkSize);
        const r = await batchApi.applyChunk(start.batchId, chunk);
        setApplied(r.applied);
      }
      setStep('result');
    } catch (e) {
      setRunError(e instanceof Error ? e.message : 'Batch update failed');
      // If a batch was created, some chunks may have applied before the failure.
      // Roll them back so we never leave partial changes behind.
      if (createdBatchId) {
        try {
          await batchApi.rollback(createdBatchId);
          setRolledBack(true);
        } catch {
          setRollbackFailed(true);
        }
      }
      setFailed(true);
      setStep('result');
    }
  }

  async function handleStop() {
    setStopping(true);
    stopRef.current = true;
  }

  async function handleUndo() {
    if (!batchId) return;
    setUndoing(true);
    try {
      await batchApi.rollback(batchId);
      setRolledBack(true);
    } catch (e) {
      setRunError(e instanceof Error ? e.message : 'Rollback failed');
    } finally {
      setUndoing(false);
    }
  }

  function handleDone() {
    onComplete();
    handleClose();
  }

  function openSkippedInNewTab() {
    try {
      localStorage.setItem(
        'cp_batch_skipped_handoff',
        JSON.stringify({ ids: skipped.map((s) => s.id), reason: skipped[0]?.reason ?? '' })
      );
    } catch {
      /* ignore storage errors */
    }
    window.open('/batch-update', '_blank', 'noopener');
  }

  const itemLabelLower = fc.label('initiative').toLowerCase();

  const replaceWarning =
    field === 'title' || field === 'description'
      ? `This will replace all existing content in ${fieldLabel} for every selected ${itemLabelLower}.`
      : null;

  const tagWarning =
    field === 'tags'
      ? tagMode === 'add'
        ? `These tags will be added to the existing tags on each selected ${itemLabelLower}.`
        : tagMode === 'replace'
        ? `All existing tags will be removed and replaced with the selected tags on each ${itemLabelLower}.`
        : `These tags will be removed from each selected ${itemLabelLower} (other tags are kept).`
      : null;

  const title =
    step === 'progress'
      ? 'Applying Batch Update…'
      : step === 'result'
      ? failed
        ? 'Batch update failed'
        : rolledBack
        ? 'Batch rolled back'
        : 'Batch update complete'
      : `Batch Update — ${selectedIds.length} ${selectedIds.length === 1 ? itemLabelLower : itemLabelLower + 's'}`;

  return (
    <Modal isOpen={isOpen} onClose={step === 'progress' ? () => {} : handleClose} title={title} size="medium">
      {/* ---------------- CONFIGURE ---------------- */}
      {step === 'configure' && (
        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-text-primary">Field to update</label>
            <select
              value={field}
              onChange={(e) => onFieldChange(e.target.value)}
              className="w-full rounded-md border border-border-default bg-surface-default px-3 py-2 text-sm text-text-primary transition-colors focus:border-border-focus focus:ring-2 focus:ring-border-focus/20"
            >
              <option value="">Select a field</option>
              <optgroup label="Main">
                {MAIN_FIELD_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </optgroup>
              <optgroup label="Scoring">
                {SCORING_FIELD_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </optgroup>
            </select>
          </div>

          {field && field !== 'tags' && (
            <>
              {field === 'userId' && (
                <Select label="New owner" value={fieldValue} onChange={(e) => setFieldValue(e.target.value)} options={ownerOptions} placeholder="Select owner (leave blank to clear)" />
              )}
              {field === 'subProductId' && (
                <Select label={`New ${fc.label('subProduct').toLowerCase()}`} value={fieldValue} onChange={(e) => setFieldValue(e.target.value)} options={subProductOptions} placeholder={`Select ${fc.label('subProduct').toLowerCase()}`} />
              )}
              {field === 'classificationId' && (
                <Select label="New classification" value={fieldValue} onChange={(e) => setFieldValue(e.target.value)} options={classificationOptions} placeholder="Select classification (leave blank to clear)" />
              )}
              {field === 'statusId' && (
                <Select label="New status" value={fieldValue} onChange={(e) => setFieldValue(e.target.value)} options={statusOptions} placeholder="Select status" />
              )}
              {field === 'startQuarter' && (
                <Select label="New start month" value={fieldValue} onChange={(e) => setFieldValue(e.target.value)} options={quarterOptions} placeholder="Select start month" />
              )}
              {field === 'endQuarter' && (
                <Select label="New end month" value={fieldValue} onChange={(e) => setFieldValue(e.target.value)} options={quarterOptions} placeholder="Select end month" helpText="Leave empty to clear the end month" />
              )}
              {field === 'isStarred' && (
                <Select label="Starred" value={fieldValue} onChange={(e) => setFieldValue(e.target.value)} options={[{ value: 'true', label: 'Starred' }, { value: 'false', label: 'Not starred' }]} />
              )}
              {field === 'title' && (
                <Input label={`New ${itemLabelLower} title`} value={fieldValue} onChange={(e) => setFieldValue(e.target.value)} maxLength={200} placeholder="Enter title" />
              )}
              {field === 'description' && (
                <Textarea label="New description" value={fieldValue} onChange={(e) => setFieldValue(e.target.value)} maxLength={500} rows={3} placeholder="Enter description (leave blank to clear)" />
              )}
              {(field === 'impact' || field === 'effort') && (
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-text-primary">New {fieldLabel} rating</label>
                  <div className="flex items-center gap-3">
                    <DotRating
                      value={fieldValue ? parseInt(fieldValue, 10) : 0}
                      color={field === 'impact' ? 'green' : 'purple'}
                      editable
                      onChange={(v) => setFieldValue(v ? String(v) : '')}
                    />
                    <span className="text-xs text-text-tertiary">
                      {fieldValue ? `${fieldValue} of 5` : 'Not rated (clears existing rating)'}
                    </span>
                  </div>
                </div>
              )}
            </>
          )}

          {field === 'tags' && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1.5">Mode</label>
                <div className="flex gap-4">
                  {(['add', 'replace', 'remove'] as TagMode[]).map((m) => (
                    <label key={m} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="radio" name="tagMode" checked={tagMode === m} onChange={() => setTagMode(m)} className="h-4 w-4" />
                      <span className="capitalize">{m}</span>
                    </label>
                  ))}
                </div>
              </div>
              <FormMultiSelect
                label="Tags"
                options={tagOptions}
                selected={selectedTagIds}
                onChange={setSelectedTagIds}
                placeholder="Select tags..."
              />
            </div>
          )}

          {tagWarning && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">{tagWarning}</p>
          )}
          {replaceWarning && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">{replaceWarning}</p>
          )}
          {configError && <p className="text-sm text-red-500">{configError}</p>}

          <ModalFooter>
            <Button variant="secondary" onClick={handleClose}>Cancel</Button>
            <Button
              variant="primary"
              onClick={() => {
                if (validateConfigure()) setStep('confirm');
              }}
            >
              Next
            </Button>
          </ModalFooter>
        </div>
      )}

      {/* ---------------- CONFIRM ---------------- */}
      {step === 'confirm' && (
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-md bg-blue-50 border border-blue-200 px-4 py-3">
            <ExclamationTriangleIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-text-primary">
              <p className="font-medium">
                {selectedIds.length} {selectedIds.length === 1 ? itemLabelLower : itemLabelLower + 's'} will be updated.
              </p>
              <p className="mt-1 text-text-secondary">
                <span className="font-medium">{fieldLabel}</span> → <span className="font-medium"><MonthLabel value={valueDisplay || '(empty)'} /></span>
              </p>
            </div>
          </div>
          {replaceWarning && <p className="text-sm text-amber-700">{replaceWarning}</p>}
          <p className="text-xs text-text-tertiary">
            Records that would become invalid (e.g. a quarter conflict) are skipped automatically and reported after the run.
            This action is fully recorded in each record's audit log and can be undone.
          </p>

          <ModalFooter>
            <Button variant="secondary" onClick={() => setStep('configure')}>Back</Button>
            <Button variant="primary" onClick={runBatch}>Confirm</Button>
          </ModalFooter>
        </div>
      )}

      {/* ---------------- PROGRESS ---------------- */}
      {step === 'progress' && (
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between text-sm mb-1.5">
              <span className="text-text-secondary">Updating records…</span>
              <span className="font-medium text-text-primary">{applied} / {total || selectedIds.length}</span>
            </div>
            <div className="h-2.5 w-full rounded-full bg-gray-200 overflow-hidden">
              <div
                className="h-full bg-blue-600 transition-all duration-300"
                style={{ width: `${total ? Math.round((applied / total) * 100) : 0}%` }}
              />
            </div>
          </div>
          {skipped.length > 0 && (
            <p className="text-xs text-amber-700">
              {skipped.length} record{skipped.length === 1 ? '' : 's'} will be skipped (details shown after the run).
            </p>
          )}
          {stopping && <p className="text-sm text-amber-700">Stopping and rolling back applied changes…</p>}
          <ModalFooter>
            {showStop ? (
              <Button variant="danger" onClick={handleStop} disabled={stopping}>
                {stopping ? 'Rolling back…' : 'Stop & roll back'}
              </Button>
            ) : (
              <span className="text-xs text-text-tertiary">Applying changes — this usually takes just a moment.</span>
            )}
          </ModalFooter>
        </div>
      )}

      {/* ---------------- RESULT ---------------- */}
      {step === 'result' && (
        <div className="space-y-4">
          {failed ? (
            <div className="flex items-start gap-3 rounded-md bg-red-50 border border-red-200 px-4 py-3">
              <ExclamationTriangleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-text-primary">The batch update failed.</p>
                {runError && <p className="mt-1 text-text-secondary">{runError}</p>}
                {rollbackFailed ? (
                  <p className="mt-1 text-red-700">Some records may have changed and automatic rollback also failed — review the audit log and undo manually.</p>
                ) : rolledBack ? (
                  <p className="mt-1 text-text-secondary">Applied records were rolled back automatically — no partial changes remain.</p>
                ) : (
                  <p className="mt-1 text-text-secondary">No changes were applied.</p>
                )}
              </div>
            </div>
          ) : rolledBack ? (
            <div className="flex items-start gap-3 rounded-md bg-amber-50 border border-amber-200 px-4 py-3">
              <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-text-primary">Changes were rolled back.</p>
                <p className="mt-1 text-text-secondary">All affected records were restored to their previous values.</p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3 rounded-md bg-green-50 border border-green-200 px-4 py-3">
              <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-text-primary">{applied} {applied === 1 ? 'record' : 'records'} updated.</p>
                <p className="mt-1 text-text-secondary"><span className="font-medium">{fieldLabel}</span> → <span className="font-medium"><MonthLabel value={valueDisplay} /></span></p>
              </div>
            </div>
          )}

          {skipped.length > 0 && (
            <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm">
              <p className="font-medium text-text-primary">
                {skipped.length} {skipped.length === 1 ? `${itemLabelLower} was` : `${itemLabelLower}s were`} skipped.
              </p>
              <p className="mt-1 text-text-secondary">
                <span className="font-medium">Reason:</span> {skipped[0].reason}
              </p>
              <button
                type="button"
                onClick={openSkippedInNewTab}
                className="mt-2 inline-flex items-center gap-1 font-medium text-amber-700 hover:text-amber-800"
              >
                <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                View skipped ({skipped.length})
              </button>
            </div>
          )}

          {runError && !failed && <p className="text-sm text-red-500">{runError}</p>}

          <ModalFooter>
            {!rolledBack && !failed && (
              <Button variant="secondary" onClick={handleUndo} disabled={undoing}>
                {undoing ? 'Undoing…' : 'Undo this batch'}
              </Button>
            )}
            <Button variant="primary" onClick={handleDone}>Done</Button>
          </ModalFooter>
        </div>
      )}
    </Modal>
  );
}
